// A playbook's skill text (knowledge/agent-playbooks/*.md) as blocks to render
// (plan M5 step 5b). Only what the playbooks use: a title, sections, paragraphs,
// ordered and unordered lists with nesting, bold and inline code. Everything
// else stays plain text; nothing is ever rendered as HTML.

export type Inline = { kind: "text" | "strong" | "code"; text: string };
export interface ListItem { text: Inline[]; children: List[] }
export interface List { ordered: boolean; items: ListItem[] }
export type Block = { kind: "paragraph"; text: Inline[] } | { kind: "list"; list: List };
/** One step of a section, from a `### Title {#step-id}` heading (plan M8 step 8b): the id ties it to the playbook's step. */
export interface SkillStep { id: string; title: string; blocks: Block[] }
/** A `##` section; `blocks` come before its first step, if it has steps. */
export interface SkillSection { title: string | null; blocks: Block[]; steps: SkillStep[] }
export interface SkillDocument { title: string | null; sections: SkillSection[] }

const ITEM = /^(\s*)([-*]|\d+\.)\s+(.*)$/;

export function inline(text: string): Inline[] {
  const parts: Inline[] = [];
  for (const piece of text.split(/(\*\*[^*]+\*\*|`[^`]+`)/)) {
    if (!piece) continue;
    if (piece.length > 4 && piece.startsWith("**") && piece.endsWith("**")) parts.push({ kind: "strong", text: piece.slice(2, -2) });
    else if (piece.length > 2 && piece.startsWith("`") && piece.endsWith("`")) parts.push({ kind: "code", text: piece.slice(1, -1) });
    else parts.push({ kind: "text", text: piece });
  }
  return parts;
}

export function parseSkill(markdown: string): SkillDocument {
  const doc: SkillDocument = { title: null, sections: [{ title: null, blocks: [], steps: [] }] };
  let section = doc.sections[0];
  // Where blocks go: the section, or its latest step.
  let target = section.blocks;
  let paragraph: string[] = [];
  // Open lists, outermost first, with the indent of their items and the text of the last item.
  let stack: { indent: number; list: List; last: { lines: string[]; item: ListItem } | null }[] = [];

  const flushParagraph = () => {
    if (paragraph.length) target.push({ kind: "paragraph", text: inline(paragraph.join(" ")) });
    paragraph = [];
  };
  const closeItem = (entry: (typeof stack)[number]) => {
    if (entry.last) entry.last.item.text = inline(entry.last.lines.join(" "));
  };
  const flushLists = () => {
    for (const entry of stack) closeItem(entry);
    stack = [];
  };

  for (const raw of markdown.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.replace(/\s+$/, "");
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    const item = ITEM.exec(line);
    if (!line.trim()) { flushParagraph(); continue; }
    if (heading) {
      flushParagraph(); flushLists();
      // A step's heading ends with its id (`### Title {#step-id}`, plan M8 step 8b); the id is for zooi, not the reader.
      const step = /^(.*\S)\s*\{#([a-z][a-z0-9_-]*)\}$/.exec(heading[2]);
      const title = step ? step[1] : heading[2];
      if (heading[1] === "#" && doc.title === null && section === doc.sections[0] && !section.blocks.length) doc.title = title;
      else if (heading[1] === "##" || heading[1] === "#") { section = { title, blocks: [], steps: [] }; doc.sections.push(section); target = section.blocks; }
      else if (step && heading[1] === "###" && section.title !== null) { const next: SkillStep = { id: step[2], title, blocks: [] }; section.steps.push(next); target = next.blocks; }
      else target.push({ kind: "paragraph", text: [{ kind: "strong", text: title }] });
      continue;
    }
    if (item) {
      flushParagraph();
      const indent = item[1].length, ordered = item[2] !== "-" && item[2] !== "*";
      while (stack.length && indent < stack[stack.length - 1].indent) closeItem(stack.pop()!);
      let top = stack[stack.length - 1];
      if (!top || indent > top.indent) {
        const list: List = { ordered, items: [] };
        if (top?.last) { closeItem(top); top.last.item.children.push(list); }
        else target.push({ kind: "list", list });
        top = { indent, list, last: null };
        stack.push(top);
      } else closeItem(top);
      const entry: ListItem = { text: [], children: [] };
      top.list.items.push(entry);
      top.last = { lines: [item[3]], item: entry };
      continue;
    }
    // An indented line under a list item continues it; anything else is a paragraph.
    const top = stack[stack.length - 1];
    if (top?.last && /^\s/.test(line)) { top.last.lines.push(line.trim()); continue; }
    flushLists();
    paragraph.push(line.trim());
  }
  flushParagraph(); flushLists();
  doc.sections = doc.sections.filter((s) => s.title !== null || s.blocks.length);
  return doc;
}
